import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import jwtAuthService from '../components/jwtAuthService';

const DeleteAccountInfo: React.FC = () => {
    const navigate = useNavigate();
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    useEffect(() => {
        const checkAuth = async () => {
            const authenticated = await jwtAuthService.isAuthenticated();
            setIsLoggedIn(authenticated);
        };
        checkAuth();
    }, []);

    const handleGoToSettings = () => {
        if (isLoggedIn) {
            navigate('/profile/account-settings');
        } else {
            navigate('/login');
        }
    };

    return (
        <div className="container py-5">
            <div className="row justify-content-center">
                <div className="col-lg-10">
                    {/* Header */}
                    <div className="text-center mb-5">
                        <h1 className="display-4 mb-3">
                            <i className="fas fa-user-slash text-danger me-3"></i>
                            Delete Your Account
                        </h1>
                        <p className="lead text-muted">
                            Learn what happens when you delete your WiZiAi account and how to proceed
                        </p>
                    </div>

                    {/* Important Notice */}
                    <div className="alert alert-warning border-warning mb-4" role="alert">
                        <h5 className="alert-heading">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            Important Information
                        </h5>
                        <p className="mb-0">
                            Account deletion is a serious action. Please read this page carefully before proceeding.
                        </p>
                    </div>

                    {/* What Happens Section */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-header bg-primary text-white">
                            <h4 className="mb-0">
                                <i className="fas fa-info-circle me-2"></i>
                                What Happens When You Delete Your Account
                            </h4>
                        </div>
                        <div className="card-body">
                            <div className="mb-4">
                                <h5 className="text-danger">
                                    <i className="fas fa-times-circle me-2"></i>
                                    Your account will be marked for deletion
                                </h5>
                                <p className="ms-4">
                                    Once you confirm deletion, your account will be immediately disabled and scheduled for permanent removal.
                                </p>
                            </div>

                            <div className="mb-4">
                                <h5 className="text-danger">
                                    <i className="fas fa-ban me-2"></i>
                                    You will be logged out immediately
                                </h5>
                                <p className="ms-4">
                                    All your active sessions across devices will be terminated, and you won't be able to log in again.
                                </p>
                            </div>

                            <div className="mb-4">
                                <h5 className="text-danger">
                                    <i className="fas fa-database me-2"></i>
                                    The following data will be permanently deleted:
                                </h5>
                                <ul className="ms-4">
                                    <li><strong>Personal Information:</strong> Name, email, profile details</li>
                                    <li><strong>Quiz History:</strong> All quizzes you've taken and your answers</li>
                                    <li><strong>Performance Analytics:</strong> AI-generated insights and progress tracking</li>
                                    <li><strong>Points & Rewards:</strong> All earned points and achievements</li>
                                    <li><strong>Leaderboard Rankings:</strong> Your position and competitive data</li>
                                    <li><strong>Subscription Data:</strong> Payment history and subscription details</li>
                                    <li><strong>Preferences:</strong> All saved settings and customizations</li>
                                </ul>
                            </div>

                            <div className="alert alert-info mb-0">
                                <h6 className="alert-heading">
                                    <i className="fas fa-clock me-2"></i>
                                    30-Day Grace Period
                                </h6>
                                <p className="mb-2">
                                    Your data will be preserved for <strong>30 days</strong> after requesting deletion. 
                                    During this time:
                                </p>
                                <ul className="mb-0">
                                    <li>Your account remains inactive (you cannot log in)</li>
                                    <li>Your data is safely stored but not accessible</li>
                                    <li>You can contact support to restore your account</li>
                                    <li>After 30 days, an administrator will permanently delete all your data</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Steps to Delete Account */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-header bg-success text-white">
                            <h4 className="mb-0">
                                <i className="fas fa-list-ol me-2"></i>
                                How to Delete Your Account
                            </h4>
                        </div>
                        <div className="card-body">
                            <div className="mb-4">
                                <h5>
                                    <span className="badge bg-primary me-2">Step 1</span>
                                    Log in to your account
                                </h5>
                                <p className="ms-5">
                                    You must be logged in to request account deletion. If you're not logged in, 
                                    {' '}
                                    {!isLoggedIn && (
                                        <button 
                                            onClick={() => navigate('/login')} 
                                            className="btn btn-sm btn-link p-0"
                                        >
                                            click here to log in
                                        </button>
                                    )}
                                    {isLoggedIn && <span className="text-success">✓ You are currently logged in</span>}
                                </p>
                            </div>

                            <div className="mb-4">
                                <h5>
                                    <span className="badge bg-primary me-2">Step 2</span>
                                    Go to Account Settings
                                </h5>
                                <p className="ms-5">
                                    Navigate to your Profile → Account Settings page. You can access it from the profile menu 
                                    or click the button at the bottom of this page.
                                </p>
                            </div>

                            <div className="mb-4">
                                <h5>
                                    <span className="badge bg-primary me-2">Step 3</span>
                                    Find the "Delete Account" section
                                </h5>
                                <p className="ms-5">
                                    Scroll down to the "Danger Zone" section at the bottom of the Account Settings page. 
                                    This section contains the delete account option.
                                </p>
                            </div>

                            <div className="mb-4">
                                <h5>
                                    <span className="badge bg-primary me-2">Step 4</span>
                                    Click "Delete My Account"
                                </h5>
                                <p className="ms-5">
                                    Click the red "Delete My Account" button. This will open a confirmation dialog.
                                </p>
                            </div>

                            <div className="mb-4">
                                <h5>
                                    <span className="badge bg-primary me-2">Step 5</span>
                                    Enter your password
                                </h5>
                                <p className="ms-5">
                                    For security verification, you'll need to enter your current account password. 
                                    Optionally, you can provide a reason for leaving to help us improve.
                                </p>
                            </div>

                            <div className="mb-4">
                                <h5>
                                    <span className="badge bg-primary me-2">Step 6</span>
                                    Confirm deletion
                                </h5>
                                <p className="ms-5">
                                    After entering your password, you'll see a final confirmation screen explaining 
                                    what will happen. Click "Yes, Delete My Account" to proceed.
                                </p>
                            </div>

                            <div>
                                <h5>
                                    <span className="badge bg-primary me-2">Step 7</span>
                                    Check your email
                                </h5>
                                <p className="ms-5">
                                    You'll receive an email confirmation with details about:
                                </p>
                                <ul className="ms-5">
                                    <li>Confirmation that your account is scheduled for deletion</li>
                                    <li>The exact date when permanent deletion will occur (30 days from now)</li>
                                    <li>Instructions on how to restore your account if you change your mind</li>
                                    <li>Support contact information</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Restore Account */}
                    <div className="card shadow-sm mb-4 border-success">
                        <div className="card-header bg-light">
                            <h4 className="mb-0 text-success">
                                <i className="fas fa-undo me-2"></i>
                                Changed Your Mind? You Can Restore Your Account
                            </h4>
                        </div>
                        <div className="card-body">
                            <p>
                                If you delete your account but later decide you want to keep it, you have <strong>30 days</strong> 
                                to request restoration.
                            </p>
                            <h6 className="mt-3">How to Restore Your Account:</h6>
                            <ol>
                                <li>Contact our support team at <a href="mailto:support@wiziai.com">support@wiziai.com</a></li>
                                <li>Include your registered email address</li>
                                <li>Request account restoration</li>
                                <li>Our admin team will restore your account within 24-48 hours</li>
                                <li>All your data will be preserved and accessible once restored</li>
                            </ol>
                            <div className="alert alert-warning mt-3 mb-0">
                                <strong>Important:</strong> After 30 days, your account and all associated data will be 
                                permanently deleted by an administrator and cannot be recovered.
                            </div>
                        </div>
                    </div>

                    {/* Alternatives Section */}
                    <div className="card shadow-sm mb-4 border-info">
                        <div className="card-header bg-light">
                            <h4 className="mb-0 text-info">
                                <i className="fas fa-lightbulb me-2"></i>
                                Before You Delete: Consider These Alternatives
                            </h4>
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <h6><i className="fas fa-pause-circle text-info me-2"></i>Take a Break</h6>
                                <p className="ms-4 mb-2">
                                    Simply stop using the platform. Your account and data will remain safe if you want to 
                                    return later.
                                </p>
                            </div>
                            <div className="mb-3">
                                <h6><i className="fas fa-bell-slash text-info me-2"></i>Disable Notifications</h6>
                                <p className="ms-4 mb-2">
                                    Adjust your notification settings in your profile to reduce emails and alerts without 
                                    deleting your account.
                                </p>
                            </div>
                            <div className="mb-3">
                                <h6><i className="fas fa-user-edit text-info me-2"></i>Update Your Privacy Settings</h6>
                                <p className="ms-4 mb-2">
                                    Modify what information is visible and how your data is used in your privacy settings.
                                </p>
                            </div>
                            <div>
                                <h6><i className="fas fa-headset text-info me-2"></i>Contact Support</h6>
                                <p className="ms-4 mb-0">
                                    If you're having issues with the platform, our support team is here to help at{' '}
                                    <a href="mailto:support@wiziai.com">support@wiziai.com</a>
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="text-center mt-5 mb-4">
                        <div className="d-flex flex-column flex-md-row gap-3 justify-content-center">
                            <button 
                                onClick={() => navigate('/')} 
                                className="btn btn-secondary btn-lg"
                            >
                                <i className="fas fa-arrow-left me-2"></i>
                                Go Back Home
                            </button>
                            
                            {isLoggedIn ? (
                                <button 
                                    onClick={handleGoToSettings}
                                    className="btn btn-danger btn-lg"
                                >
                                    <i className="fas fa-user-slash me-2"></i>
                                    Proceed to Delete Account
                                </button>
                            ) : (
                                <button 
                                    onClick={() => navigate('/login')}
                                    className="btn btn-primary btn-lg"
                                >
                                    <i className="fas fa-sign-in-alt me-2"></i>
                                    Log In to Continue
                                </button>
                            )}
                        </div>
                        
                        <p className="text-muted mt-3 mb-0">
                            <small>
                                <i className="fas fa-question-circle me-1"></i>
                                Need help? Contact us at <a href="mailto:support@wiziai.com">support@wiziai.com</a>
                            </small>
                        </p>
                    </div>

                    {/* FAQ Section */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-header bg-secondary text-white">
                            <h4 className="mb-0">
                                <i className="fas fa-question-circle me-2"></i>
                                Frequently Asked Questions
                            </h4>
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <h6 className="text-primary">Can I temporarily deactivate my account instead?</h6>
                                <p className="ms-3">
                                    Currently, we only offer permanent deletion with a 30-day grace period. However, you can 
                                    simply stop using the platform without deleting your account.
                                </p>
                            </div>
                            
                            <div className="mb-3">
                                <h6 className="text-primary">Will my username be available for others after deletion?</h6>
                                <p className="ms-3">
                                    After permanent deletion (30 days), your username may become available for new registrations.
                                </p>
                            </div>
                            
                            <div className="mb-3">
                                <h6 className="text-primary">What happens to my subscription if I delete my account?</h6>
                                <p className="ms-3">
                                    Any active subscription will be cancelled when you delete your account. Since we don't have 
                                    recurring payments, there are no refunds for unused time.
                                </p>
                            </div>
                            
                            <div className="mb-3">
                                <h6 className="text-primary">Can I download my data before deleting?</h6>
                                <p className="ms-3">
                                    Currently, we don't offer a data export feature. If you need specific data, please contact 
                                    support before deleting your account.
                                </p>
                            </div>
                            
                            <div className="mb-3">
                                <h6 className="text-primary">How long does the deletion process take?</h6>
                                <p className="ms-3">
                                    Your account is immediately disabled when you request deletion. Permanent deletion occurs 
                                    after 30 days when an administrator manually processes it.
                                </p>
                            </div>
                            
                            <div className="mb-0">
                                <h6 className="text-primary">Will I receive any notification before permanent deletion?</h6>
                                <p className="ms-3 mb-0">
                                    You will receive an initial email when you request deletion with the final deletion date. 
                                    Currently, we don't send reminder emails before the 30-day period ends.
                                </p>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default DeleteAccountInfo;
